import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Gate, UserAction } from '../api';
import './GateMarker.css';
import { GateArtwork } from './GateArtwork';
import { observeElementResize } from './observeElementResize';

type GateMarkerProps = {
  gate: Gate;
  className?: string;
  title?: string;
  onClickStopPropagation?: boolean;
  alt?: string;
  imageClassName?: string;
  language?: string;
  focusable?: boolean;
  interactiveTooltip?: boolean;
  actions?: UserAction[];
  loading?: boolean;
  onExecuteAction?: (actionId: string) => void;
};

const noActions: UserAction[] = [];

export const getGateImagePath = (gate: Gate, language: string = 'En') => {
  if (gate.markerId && lurkerImages[gate.markerId]) {
    return `/Gates/lurker/${lurkerImages[gate.markerId]}`;
  }
  const lang = language === 'Ru' ? 'ru/' : '';
  return `/Gates/${lang}${encodeURIComponent(gate.otherWorld)}_${encodeURIComponent(gate.dimensionSign)}.png`;
}

export const lurkerImages: Record<string, string> = {
  LurkerAnotherDimensionMoving: 'Another_Dimension_Square_Moving.png',
  LurkerAnotherTimeBlood: 'Another_Time_Diamond_Blood.png',
  LurkerCityOfTheGreatRaceMonstrous: 'City_of_the_Great_Race_Triangle_Monster.png',
  LurkerDreamlandsMoving: 'Dreamlands_Slash_Moving.png',
  LurkerGreatHallOfCeleanoMadness: 'Great_Hall_of_Celeano_Start_Madness.png',
  LurkerLostCarcosaMadness: 'Lost_Carcosa_Circle_Madness.png',
  LurkerPlateauOfLengBlood: 'Plateau_of_Leng_Diamond_Blood.png',
  LurkerRlyehDevouring: 'Rlyeh_Plus_Devour.png',
  LurkerAbyssDoom: 'The_Abyss_Hexagon_Doom.png',
  LurkerTheUnderworldBlood: 'The_Underworld_Hexagon_Blood.png',
  LurkerUnknownKadathMadness: 'Unknown_Kadath_Triangle_Madness.png',
  LurkerYuggothEndless: 'Yuggoth_Circle_Endless.png',
  LurkerSplitLostCarcosaAnotherTime: 'Split_Lost_Carcosa_Plus_Another_Time_Square.png',
  LurkerSplitPlateauOfLengDreamlands: 'Split_Plateau_of_Leng_Diamond_Dreamlands_Slash.png',
  LurkerSplitGreatHallOfCeleanoCityOfTheGreatRace: 'Split_Great_Hall_of_Celeano_Star_The_City_of_the_Greate_Race_Triangle.png',
  LurkerSplitUnknownKadathTheUnderworld: 'Split_Unknown_Kadath_Slash_The_Underworld_Star.png',
  LurkerSplitAbyssRlyeh: 'Split_The_Abyss_Hexagon_Rlyeh_Plus.png',
  LurkerSplitYuggothAnotherDimension: 'Split_Yuggoth_Circle_Another_Dimension_Square.png',
};

export const getGateDestinations = (gate: Gate) =>
  gate.secondaryOtherWorld ? [gate.otherWorld, gate.secondaryOtherWorld] : [gate.otherWorld];

const abilityDescriptions: Record<string, string> = {
  Devouring: 'Devouring Gate: Investigators at this location when the gate opens are devoured.',
  Doom: 'Gate of Doom: If any investigator is here when the gate opens, add one extra doom token.',
  Endless: 'Endless Gate: Cannot be collected as a gate trophy. When closed or sealed, shuffle it back into the gate marker stack.',
  Monstrous: 'Monstrous Gate: If an investigator fails the check to close this gate, a monster appears here (in the Outskirts if the monster limit is exceeded).',
  Blood: 'Gate of Blood: If an investigator fails the check to close this gate, lose 1 Stamina.',
  Madness: 'Gate of Madness: If an investigator fails the check to close this gate, lose 1 Sanity.',
  Moving: 'Moving Gate: When its symbol activates during monster movement, move along the matching arrow. The first investigator chooses the order of multiple Moving Gates. Gates and vortices block movement. Investigators at the destination are pulled through and delayed; investigators left behind lose their explored token.',
};

export const getGateAbilityDescription = (gate: Gate) => [
  gate.ability ? abilityDescriptions[gate.ability] : '',
  gate.secondaryOtherWorld ? 'Split Gate: Choose either Other World when entering. Return from either to this gate and receive an explored token; exploring either is enough to close it. Closing returns monsters with either symbol from all cities, Sky, and Outskirts to the cup.' : '',
].filter(Boolean).join('\n');

export const getGateTooltip = (gate: Gate) => [
  `Gate to ${getGateDestinations(gate).join(' / ')}`,
  `Modifier: ${gate.modifier >= 0 ? '+' : ''}${gate.modifier}`,
  `Sign: ${[gate.dimensionSign, gate.secondaryDimensionSign].filter(Boolean).join(' / ')}`,
  getGateAbilityDescription(gate),
].filter(Boolean).join('\n');

export const GateMarker: React.FC<GateMarkerProps> = ({
  gate,
  className = 'trophy-chip gate',
  title,
  onClickStopPropagation = false,
  alt,
  imageClassName,
  language = 'En',
  focusable = false,
  interactiveTooltip = false,
  actions = noActions,
  loading = false,
  onExecuteAction
}) => {
  const [open, setOpen] = useState(false);
  const markerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const [position, setPosition] = useState({ left: 8, top: 8 });
  const hasActions = actions.length > 0 && !!onExecuteAction;
  const label = `${getGateDestinations(gate).join(' / ')} gate details`;
  const abilityDescription = getGateAbilityDescription(gate);
  const tooltip = title
    ? title + (abilityDescription && !title.includes(abilityDescription) ? `\n${abilityDescription}` : '')
    : getGateTooltip(gate);
  useLayoutEffect(() => {
    if (!interactiveTooltip || !open || !markerRef.current || !tooltipRef.current) return;
    const positionPopup = () => {
      if (!markerRef.current || !tooltipRef.current) return;
      const anchor = markerRef.current.getBoundingClientRect();
      const size = tooltipRef.current.getBoundingClientRect();
      setPosition({
        left: Math.max(8, Math.min(anchor.left, window.innerWidth - size.width - 8)),
        top: Math.max(8, Math.min(anchor.bottom + 4, window.innerHeight - size.height - 8)),
      });
    };
    positionPopup();
    const disconnect = observeElementResize(tooltipRef.current, positionPopup);
    const dismiss = () => {
      if (tooltipRef.current?.contains(document.activeElement)) markerRef.current?.focus({ preventScroll: true });
      setOpen(false);
    };
    const outsideClick = (event: MouseEvent) => {
      if (!markerRef.current?.contains(event.target as Node) && !tooltipRef.current?.contains(event.target as Node)) dismiss();
    };
    const scroll = (event: Event) => {
      if (!(event.target instanceof Node) || !tooltipRef.current?.contains(event.target)) dismiss();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      dismiss();
      markerRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('click', outsideClick, true);
    document.addEventListener('keydown', escape, true);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', dismiss);
    return () => {
      disconnect();
      document.removeEventListener('click', outsideClick, true);
      document.removeEventListener('keydown', escape, true);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [interactiveTooltip, open, tooltip, actions, loading, hasActions]);
  useLayoutEffect(() => {
    if (interactiveTooltip && open && hasActions) tooltipRef.current?.focus({ preventScroll: true });
  }, [interactiveTooltip, open, hasActions]);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClickStopPropagation || interactiveTooltip) {
      event.stopPropagation();
    }
    if (interactiveTooltip) setOpen(value => !value);
  };

  return (
    <><div
      ref={markerRef}
      className={`${className}${interactiveTooltip ? ' gate-tooltip-trigger' : ''}`}
      title={interactiveTooltip && open ? undefined : tooltip}
      role={interactiveTooltip ? 'button' : undefined}
      aria-label={interactiveTooltip ? label : undefined}
      aria-expanded={interactiveTooltip ? open : undefined}
      aria-haspopup={interactiveTooltip && hasActions ? 'dialog' : undefined}
      aria-controls={interactiveTooltip && open && hasActions ? tooltipId : undefined}
      aria-describedby={interactiveTooltip && open && !hasActions ? tooltipId : undefined}
      tabIndex={interactiveTooltip || focusable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={interactiveTooltip ? event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          if (!event.repeat) setOpen(value => !value);
        }
      } : undefined}
    >
      <GateArtwork gate={gate}
        classicPath={getGateImagePath(gate, language)}
        alt={alt ?? `${getGateDestinations(gate).join(' / ')} gate`}
        className={imageClassName}
      />
    </div>
    {interactiveTooltip && open && createPortal(
      <div ref={tooltipRef} id={tooltipId} role={hasActions ? 'dialog' : 'tooltip'}
        aria-label={hasActions ? label : undefined} tabIndex={hasActions ? -1 : undefined}
        className="gate-tooltip" style={position} onClick={event => event.stopPropagation()}>
        <div>{tooltip}</div>
        {hasActions && <div className="gate-tooltip-actions">
          {actions.map(action => <div key={action.id} className="action-card-wrapper action-selection-surface"
            aria-disabled={loading || !!action.disabled}
            style={action.color ? { '--action-color': action.color } as React.CSSProperties : undefined}>
            <button type="button" className={`action-button${action.color ? ' has-color' : ''}`}
              title={action.description} disabled={loading || !!action.disabled}
              onClick={event => {
                event.stopPropagation();
                if (loading || action.disabled) return;
                onExecuteAction!(action.id);
                setOpen(false);
                markerRef.current?.focus({ preventScroll: true });
              }}>
              <div className="action-card-text"><div className="action-name">{action.description || action.name}</div></div>
            </button>
          </div>)}
        </div>}
      </div>, document.body)}
    </>
  );
};
